import { FC, ReactNode, useEffect, useState } from 'react';

import { IconButton } from '@pp/core/src/components/controls/iconButton/IconButton';
import { RadioGroup } from '@pp/core/src/components/controls/radioGroup/RadioGroup';
import { Icons } from '@pp/core/src/components/icons/iconNames';
import { L10nText } from '@pp/core/src/services/uiTranslations/reactBindings/L10nText';

import { useGameDispatch } from '../../../store/GameStore';
import { highflyerGameStateActions } from '../../../store/slices/highflyerGame/slice';

import styles from './HeaderNavigationCarousel.module.css';

interface TabProps {
  label: ReactNode;
  content: ReactNode;
}

const CALENDAR_PERIOD = {
  STATISTICS_DAY: 'day',
  STATISTICS_MONTH: 'month',
  STATISTICS_YEAR: 'year',
} as const;

interface HeaderNavigationCarouselProps {
  tabs: TabProps[];
  calendarOptions?: string[];
  initActiveIndex?: number;
  showStaticData?: boolean;
}

export const HeaderNavigationCarousel: FC<HeaderNavigationCarouselProps> = ({
  tabs,
  calendarOptions,
  initActiveIndex = 0,
  showStaticData = false,
}) => {
  const [activeIndex, setActiveIndex] = useState(initActiveIndex);
  const dispatch = useGameDispatch();
  const [isAtStart, isAtEnd] = [activeIndex === 0, activeIndex === tabs.length - 1];

  const handleItemClick = (status: 'previous' | 'next') => {
    setActiveIndex((prevIndex) => Math.max(0, Math.min(prevIndex + (status === 'next' ? 1 : -1), tabs.length - 1)));
  };

  useEffect(() => {
    dispatch(highflyerGameStateActions.setCurrentCalendarOption(CALENDAR_PERIOD.STATISTICS_DAY));
  }, [activeIndex, dispatch]);

  return (
    <>
      <div className={styles.header_navigation_container}>
        <IconButton icon={Icons.ChevronLeft} onClick={() => handleItemClick('previous')} disabled={isAtStart} />
        <span className={styles.header_navigation_title}>{tabs[activeIndex].label}</span>
        <IconButton icon={Icons.ChevronRight} onClick={() => handleItemClick('next')} disabled={isAtEnd} />
      </div>
      {calendarOptions && (
        <div key={activeIndex} className={styles.calendarOptions_container}>
          <RadioGroup
            value={calendarOptions[0]}
            name="calendarValue"
            disabled={false}
            onChange={(val): void => {
              dispatch(
                highflyerGameStateActions.setCurrentCalendarOption(CALENDAR_PERIOD[val as keyof typeof CALENDAR_PERIOD])
              );
            }}
            className={styles.radioGroup__grid}
            options={calendarOptions.map((el) => ({ label: showStaticData ? el : <L10nText tk={el} />, value: el }))}
          />
        </div>
      )}
      {tabs[activeIndex].content}
    </>
  );
};
